import { useEffect, useMemo, useRef, useState } from 'react'
import { LocaleContext, langFromQuery, translator, useT } from '@/i18n'
import { api, type TableState } from '@/lib/api'
import { DISPLAY_UTC_OFFSET_HOURS, displayNumeric, ODD_OF, ticketRef, trendWinners, type DrawnRound } from '@/lib/keno-view'

type Kind = 'results' | 'statement' | 'statement-day' | 'rules' | 'announcement'

/** The reference pop-up windows, opened by the board with window.open(). */
export function WindowPage({ kind }: { kind: Kind }) {
  const langId = useMemo(langFromQuery, [])
  const t = useMemo(() => translator(langId), [langId])
  useEffect(() => {
    document.body.classList.add(`keno-window-${kind}`)
    return () => document.body.classList.remove(`keno-window-${kind}`)
  }, [kind])

  return (
    <LocaleContext.Provider value={{ langId, t }}>
      {kind === 'results' && <ResultsWindow />}
      {kind === 'statement' && <StatementWindow />}
      {kind === 'statement-day' && <StatementDayWindow />}
      {kind === 'rules' && <RulesWindow />}
      {kind === 'announcement' && <>WL KENO</>}
    </LocaleContext.Provider>
  )
}

/** Reference links carry an href; without one bootstrap restyles them (a:not([href])). */
const link = (fn: () => void) => ({ href: '#', onClick: (e: { preventDefault: () => void }) => { e.preventDefault(); fn() } })

function closePage() {
  window.close()
  if (!window.closed) window.history.back()
}

function TopBar({ title }: { title?: string }) {
  return (
    <div className="top-navbar">
      <a {...link(closePage)} className="back-arrow"><i className="icon dripicons-chevron-left" /></a>
      {title && <>{'\n\t\t'}{title}</>}
    </div>
  )
}

const BKK = DISPLAY_UTC_OFFSET_HOURS * 3600_000
const pad = (n: number) => String(n).padStart(2, '0')
function bangkokParts(ms: number) {
  const d = new Date(ms + BKK)
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate(), hh: d.getUTCHours(), mm: d.getUTCMinutes(), ss: d.getUTCSeconds() }
}
const todayIso = () => { const p = bangkokParts(Date.now()); return `${p.y}-${pad(p.m)}-${pad(p.d)}` }
const isoToDmy = (iso: string) => { const [y, m, d] = iso.split('-'); return `${d}-${m}-${y}` }

// ── ผลลัพธ์ (gamehistory) ──────────────────────────────────────────────────────

function pageList(page: number, last: number): (number | '...')[] {
  if (last <= 4) return Array.from({ length: last }, (_, i) => i + 1)
  const start = Math.max(1, Math.min(page - 1, last - 3))
  const run = [start, start + 1, start + 2].filter((p) => p < last)
  return [...run, ...(run[run.length - 1]! < last - 1 ? ['...' as const] : []), last]
}

function ResultsWindow() {
  const query = new URLSearchParams(window.location.search)
  const [tables, setTables] = useState<TableState[] | null>(null)
  const [gameId, setGameId] = useState(query.get('ref') ?? '')
  const [date, setDate] = useState(todayIso)
  const [page, setPage] = useState(1)
  const [filterOpen, setFilterOpen] = useState(true)
  const [data, setData] = useState<{ items: DrawnRound[]; total: number; limit: number } | null>(null)
  const [error, setError] = useState(false)
  const [applied, setApplied] = useState<{ gameId: string; date: string } | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  useEffect(() => {
    api.tables().then((r) => {
      setTables(r.tables)
      const id = r.tables.some((x) => x.gameId === gameId) ? gameId : r.tables[0]?.gameId ?? ''
      setGameId(id)
      setApplied({ gameId: id, date })
    }).catch(() => setError(true))
    // First load only — later searches go through the submit button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!applied?.gameId) return
    setData(null)
    setError(false)
    api.history(applied.gameId, applied.date, page).then(setData).catch(() => setError(true))
  }, [applied, page])

  const gCode = tables?.find((x) => x.gameId === applied?.gameId)?.gCode ?? ''
  const last = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1

  return (
    <>
      <TopBar />
      <div className={`thefilter-container text-center ${filterOpen ? 'active' : ''}`}>
        <div className="container">
          <a className={`showhidefilter ${filterOpen ? 'active' : ''}`} {...link(() => setFilterOpen((o) => !o))} />
          <form id="form" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied({ gameId, date }) }}>
            <div className="filter-title" />
            <select className="form-control" id="ref" name="ref" value={gameId} onChange={(e) => setGameId(e.target.value)}>
              {(tables ?? []).map((x) => <option key={x.gameId} value={x.gameId}>{x.name}</option>)}
            </select>
            <div className="filter-title" />
            <div className="input-group">
              <input id="resultdate" name="resultdate" type="text" className="form-control dpicker" placeholder="Result Date" aria-label="Result Date" value={isoToDmy(date)} readOnly onClick={() => picker.current?.showPicker?.()} />
              <div className="input-group-append">
                <label className="input-group-text" htmlFor="resultdate" onClick={() => picker.current?.showPicker?.()}><i className="icon dripicons-calendar" /></label>
              </div>
              <input ref={picker} type="date" value={date} max={todayIso()} onChange={(e) => e.target.value && setDate(e.target.value)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }} tabIndex={-1} aria-hidden />
            </div>
            <div style={{ padding: '10px 0' }}>
              <a className="submitbutt" {...link(() => { setPage(1); setApplied({ gameId, date }) })} />
            </div>
          </form>
        </div>
      </div>
      <div className="container">
        <div style={{ clear: 'both', display: 'block' }} />
        <div className="mobileoverflow" style={{ width: '100%', marginTop: 30 }}>
          <table className="tablestyle">
            <thead>
              <tr>
                <th />
                <th style={{ width: 95, minWidth: 95, maxWidth: 95 }} />
                <th />
                {Array.from({ length: 6 }, (_, i) => <th className="info-td-o" key={i} />)}
              </tr>
            </thead>
            <tbody>
              {data?.items.map((r) => {
                const p = bangkokParts(Date.parse(r.resultAt))
                return (
                  <tr key={r.roundNumber}>
                    <td>{gCode}{r.roundNumber}</td>
                    <td>{`${pad(p.d)}-${pad(p.m)}-${p.y}`}<br />{`${pad(p.hh)}:${pad(p.mm)}:${pad(p.ss)}`}</td>
                    <td className="info-td"><span>{r.draw.sum}</span></td>
                    {trendWinners(r.draw).map((w) => (
                      <td className="info-td-o" key={w}><span className={`resulticon ${w}`}><div className="toggle-tooltip" /></span></td>
                    ))}
                    <td className="draw-results">{r.draw.numbers.join(',')}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {!data && !error && <div className="loading"><div className="loading-icon" /></div>}
          {error && <div className="keno-load-error">Cannot load results. <a onClick={() => setApplied((a) => (a ? { ...a } : a))}>Retry</a></div>}
        </div>
        {data && data.total > data.limit && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
            <ul className="pagination">
              {pageList(page, last).map((p, i) => (
                p === '...' ? (
                  <li key={`gap${i}`} className="paginate_button page-item disabled"><a className="page-link">...</a></li>
                ) : (
                  <li key={p} className={`paginate_button page-item ${p === page ? 'active' : ''}`}>
                    <a className="page-link" {...link(() => setPage(p))}>{p}</a>
                  </li>
                )
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}

// ── รายงานการเงิน (mystatement) ───────────────────────────────────────────────

function monthRange(offset: number): { key: string; label: string; first: string } {
  const p = bangkokParts(Date.now())
  const d = new Date(Date.UTC(p.y, p.m - 1 + offset, 1))
  const y = d.getUTCFullYear()
  const m = d.getUTCMonth() + 1
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const yy = String(y).slice(2)
  return { key: `${y}-${pad(m)}`, label: `01/${pad(m)}/${yy} - ${lastDay}/${pad(m)}/${yy}`, first: `01/${pad(m)}/${y}` }
}

function StatementWindow() {
  const t = useT()
  const months = useMemo(() => [monthRange(-1), monthRange(0)], [])
  const query = new URLSearchParams(window.location.search)
  const lang = query.get('l') ?? ''
  const [month, setMonth] = useState(months.find((m) => m.key === query.get('month')) ?? months[1]!)
  const [username, setUsername] = useState('')
  const [rows, setRows] = useState<{ day: string; bets: number; stake: string; returned: string }[] | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => { api.me().then((m) => setUsername(m.member.username)).catch(() => {}) }, [])
  useEffect(() => {
    setRows(null)
    setError(false)
    api.statement(month.key).then(setRows).catch(() => setError(true))
  }, [month])

  let running = 0
  return (
    <>
      <TopBar title={`${t('menustatement')} [${username}]`} />
      <div className="container" style={{ paddingBottom: 50 }}>
        <MonthFilter months={months} active={month.key} onPick={setMonth} />
        <div className="dotted lrspan"><span>Opening</span><span>{month.first}</span></div>
        <div className="balancetxt lrspan"><span /><span>0.00</span></div>
        {rows?.map((r) => {
          const wl = Number(r.returned) - Number(r.stake)
          running += wl
          const [y, m, d] = r.day.split('-')
          return (
            <a key={r.day} href={`/statement-day?date=${r.day}&month=${month.key}&l=${lang}`} className="statement-card">
              <div className="card-title lrspan">
                <table width="100%">
                  <tbody>
                    <tr>
                      <td style={{ textAlign: 'left' }}>{t('BETSTATEMENT')}</td>
                      <td style={{ textAlign: 'right' }}>{`${d}-${m}-${y}  ${weekday(t, r.day)}`}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="lrspan">
                <div>{t('WINLOSE')}:<Money v={wl} /></div>
                <div>{t('BALANCE')}:<Money v={running} /></div>
              </div>
            </a>
          )
        })}
        {!rows && !error && <div className="loading"><div className="loading-icon" /></div>}
        {error && <div className="keno-load-error">Cannot load the statement. <a {...link(() => setMonth({ ...month }))}>Retry</a></div>}
      </div>
    </>
  )
}

type Month = { key: string; label: string; first: string }

function MonthFilter({ months, active, onPick, day }: {
  months: Month[]
  active: string | null
  onPick: (m: Month) => void
  day?: string
}) {
  return (
    <div className="day-filter">
      <div className="day-filter-cont">
        {months.map((m) => (
          <a key={m.key} className={`day-filter-butt ${m.key === active ? 'active' : ''}`} {...link(() => onPick(m))}>{m.label}</a>
        ))}
        {day && <>{' '}<span className="day-filter-butt"> &gt; </span> <span className="day-filter-butt">{day}</span></>}
      </div>
    </div>
  )
}

/** Amounts the reference colours by sign: green for a gain (or nothing), red for a loss. */
function Money({ v }: { v: number }) {
  return <span style={{ color: v < 0 ? 'red' : 'green' }}>{displayNumeric(v, 2, true)}</span>
}

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
function weekday(t: (k: string) => string, isoDay: string): string {
  const [y, m, d] = isoDay.split('-').map(Number) as [number, number, number]
  return t(WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? 'SUN')
}

// ── รายงานการเดิมพัน รายวัน (the reference mybets.php) ─────────────────────────

type DayData = Awaited<ReturnType<typeof api.statementDay>>
type DayBet = DayData['items'][number]

export function StatementDayWindow() {
  const t = useT()
  const query = new URLSearchParams(window.location.search)
  const asked = query.get('date') ?? ''
  const date = /^\d{4}-\d{2}-\d{2}$/.test(asked) ? asked : todayIso()
  const lang = query.get('l') ?? ''
  const months = useMemo(() => [monthRange(-1), monthRange(0)], [])
  const [page, setPage] = useState(1)
  const [attempt, setAttempt] = useState(0)
  const [data, setData] = useState<DayData | null>(null)
  const [error, setError] = useState(false)
  const [open, setOpen] = useState<{ id: string; x: number; y: number; arrow: number } | null>(null)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setData(null)
    setError(false)
    api.statementDay(date, page).then(setData).catch(() => setError(true))
  }, [date, page, attempt])

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(null)
    const id = window.setTimeout(() => document.addEventListener('click', close), 0)
    return () => { window.clearTimeout(id); document.removeEventListener('click', close) }
  }, [open])

  const [y, m, d] = date.split('-')
  const dmy = `${d}-${m}-${y}`
  const last = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1
  const wlOf = (b: DayBet) => Number(b.winAmount ?? 0) - Number(b.amount)

  function showResult(target: HTMLElement, id: string) {
    const c = container.current?.getBoundingClientRect()
    if (!c) return
    const r = target.getBoundingClientRect()
    const width = 276
    const x = Math.max(0, Math.min(r.left + r.width / 2 - width / 2 - c.left, c.width - width))
    setOpen({ id, x, y: r.bottom - c.top, arrow: r.left + r.width / 2 - c.left - x - 8 })
  }

  const openBet = data?.items.find((b) => b.betId === open?.id)
  return (
    <>
      <div className="top-navbar">
        <a {...link(closePage)} className="back-arrow"><i className="icon dripicons-chevron-left" /></a>
        {'\n    '}{t('BETSTATEMENT')}{'    '}{dmy}
      </div>
      <div className="container" ref={container} style={{ position: 'relative' }}>
        <MonthFilter
          months={months}
          active={null}
          day={dmy}
          onPick={(mo) => { window.location.href = `/statement?month=${mo.key}&l=${lang}` }}
        />
        <table className="tablestyle">
          <thead>
            <tr>
              <th>{t('REF')}</th>
              <th style={{ width: 100, minWidth: 100, maxWidth: 100 }}>{t('DATETIME')}</th>
              <th>{t('GAME')}</th>
              <th>{t('DRAWID')}</th>
              <th>{t('BETTYPE')}</th>
              <th className="text-right">{t('STAKE')}</th>
              <th>{t('BETTXT')}</th>
              <th>{t('ODDS')}</th>
              <th className="text-right">{t('WINLOSE')}</th>
              <th>{t('menuresult')}</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((b) => {
              const odd = ODD_OF[b.betType]
              const p = bangkokParts(Date.parse(b.createdAt))
              const h12 = p.hh % 12 || 12
              return (
                <tr key={b.betId}>
                  <td>{ticketRef(b.ticketNo, b.betId)}</td>
                  <td>{`${pad(p.d)}-${pad(p.m)}-${p.y}`}<br />{`${pad(h12)}:${pad(p.mm)}:${pad(p.ss)} ${p.hh < 12 ? 'AM' : 'PM'}`}</td>
                  <td>{b.table}</td>
                  <td>{b.roundLabel}</td>
                  <td>{t(`${odd}group`)}</td>
                  <td className="text-right">{displayNumeric(b.amount, 2, true)}</td>
                  <td>{t(odd)}</td>
                  <td>{Number(b.payoutRate).toFixed(2)}</td>
                  <td className="text-right"><Money v={wlOf(b)} /></td>
                  <td>
                    {b.draw && (
                      <a tabIndex={0} role="button" href="#result" style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.preventDefault(); showResult(e.currentTarget, b.betId) }}>
                        {'\n            '}{t('menuresult')}
                      </a>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
          {data && (
            <tfoot>
              <tr>
                <td colSpan={5} style={{ textAlign: 'right' }}>{t('GRANDTOTAL')}</td>
                <td className="text-right">{displayNumeric(data.stake, 2, true)}</td>
                <td /><td />
                <td className="text-right"><Money v={Number(data.returned) - Number(data.stake)} /></td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
        {!data && !error && <div className="loading"><div className="loading-icon" /></div>}
        {error && <div className="keno-load-error">Cannot load the bets. <a {...link(() => setAttempt((n) => n + 1))}>Retry</a></div>}
        {data && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
            <ul className="pagination">
              {pageList(page, last).map((p, i) => (
                p === '...' ? (
                  <li key={`gap${i}`} className="paginate_button page-item disabled"><a className="page-link">...</a></li>
                ) : (
                  <li key={p} className={`paginate_button page-item ${p === page ? 'active' : ''}`}>
                    <a className="page-link" {...link(() => setPage(p))}>{p}</a>
                  </li>
                )
              ))}
            </ul>
          </div>
        )}
        {open && openBet?.draw && (
          <div className="popover fade bs-popover-bottom show" role="tooltip" x-placement="bottom"
            style={{ position: 'absolute', willChange: 'transform', top: 0, left: 0, transform: `translate3d(${open.x}px, ${open.y}px, 0px)` }}
            onClick={(e) => e.stopPropagation()}>
            <div className="arrow" style={{ left: open.arrow }} />
            <div className="popover-body"><DrawResult draw={openBet.draw} /></div>
          </div>
        )}
      </div>
    </>
  )
}

/** The reference's result popover body (resulttype2): sum, the 20 in order, the five outcomes. */
function DrawResult({ draw }: { draw: NonNullable<DayBet['draw']> }) {
  const t = useT()
  const s = draw.numbers.slice().sort((a, b) => a - b)
  return (
    <div className="resulttype2">
      <div className="theresult">
        <div style={{ display: 'inline-block', width: '100%' }}>
          <span className="sum">{draw.sum}</span>
          <div className="trendsnumber">
            {Array.from({ length: 10 }, (_, i) => {
              const a = s[i] ?? 0
              const b = s[10 + i] ?? 0
              return (
                <div className="tilecolumn" key={i}>
                  <div className="tile"><span className={a >= 41 ? 'lighter' : undefined}>{a}</span></div>
                  <div className="tile"><span className={b >= 41 ? 'lighter' : undefined}>{b}</span></div>
                </div>
              )
            })}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          {trendWinners(draw).map((w) => <span className={`trendstat ${w}`} key={w}><span>{t(w)}</span></span>)}
        </div>
        <div className="clearfix" />
      </div>
    </div>
  )
}

// ── กฎ (rules) ─────────────────────────────────────────────────────────────────

function RulesWindow() {
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState(false)
  useEffect(() => {
    // Our own static fragment (scripts and handlers stripped at build), not user content.
    // Not under public/rules/: a directory there turns the /rules route into nginx's
    // directory redirect and a 403.
    fetch('/assets/rules-th.html').then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then(setHtml).catch(() => setError(true))
  }, [])
  return (
    <>
      <TopBar />
      {html !== null && <div dangerouslySetInnerHTML={{ __html: html }} />}
      {html === null && !error && <div className="loading"><div className="loading-icon" /></div>}
      {error && <div className="keno-load-error">Cannot load the rules.</div>}
    </>
  )
}
